import axios from 'axios';
import axiosInstance from './axios';
import * as LocalStorageService from "../utils/localStorageService";
import { processTemplateImages } from '../utils/imageUploadUtil';

const BaseUrl = 'api/v1/store-list/';

export const getStores = async () => axiosInstance.get(`${BaseUrl}?order=true`);

export const getStorePerspectiveUsers = async (data, param) =>
  axiosInstance.post(`/api/v1/prospective-host/${param ? param : ''}`, data);

export const sendStorePerspectiveUsersMail = async (data, param) =>
  axiosInstance.post(
    `/api/v1/prospective-host-email/${param ? param : ''}`,
    data
  );

export const getCommissionAdmin = async () =>
  axiosInstance.get(`/api/v1/commission-percentage/`);

export const postCommissionAdmin = async (data) =>
  axiosInstance.post(`/api/v1/commission-percentage/`, data);

export const getSoftReviewStore = async (param) =>
  axiosInstance.get(`/api/v1/soft-review/${param}`);

export const postSoftReviewStore = async (body) => {
  const response = await axios({
    method: 'post',
    url: `${process.env.REACT_APP_BASE_URL}/api/v1/soft-review/`,
    data: body,
  });
  return response;
};

export const getPublicSoftReview = async (param) => {
  const response = await axios({
    method: 'get',
    url: `${process.env.REACT_APP_BASE_URL}/api/v1/soft-review-user/${param}`,
  });
  return response;
};

export const editSoftReviewStore = async (body, param) => {
  const response = await axios({
    method: 'patch',
    url: `${process.env.REACT_APP_BASE_URL}/api/v1/soft-review/${param}/`,
    data: body,
  });
  return response;
};

export const getProductListStore = async (param) =>
  axiosInstance.get(`/api/v1/products/${param}`);

export const updateProductTag = async (id, data) => {
  let response = axiosInstance.patch(`/api/v1/products/${id} `, data);
  return response;
};

export const getProductListTagStore = async (param) =>
  axiosInstance.get(`/api/v1/product-tags/${param}`);

export const addProductListTagStore = async (data) =>
  axiosInstance.post(`/api/v1/product-tags/`, data);

export const updateProductListTagStore = async (id, data) =>
  axiosInstance.patch(`/api/v1/product-tags/${id}/`, data);

export const removeProductListTagStore = async (id) =>
  axiosInstance.delete(`/api/v1/product-tags/${id}/`);

export const getRoleByStoreAdmin = async (param) =>
  axiosInstance.get(`/api/v1/user/admin/${param}/`);

export const addRoleByStoreAdmin = (data) =>
  axiosInstance.post(`/api/v1/user/admin/`, data);

export const updateRoleByStoreAdmin = (role, id) =>
  axiosInstance.put(`/api/v1/user/admin/${id}`, role);

export const reinviteRoleByStoreAdmin = async (uid) =>
  axiosInstance.get(`/api/v1/user/re-invite/${uid}`);

export const downloadMedias = async () =>
  axiosInstance.post(`/api/v1/download-media/`);

export const completeAddRoleByStoreAdmin = async (data, passuid, passtoken) =>
  axios.post(
    `${process.env.REACT_APP_BASE_URL}/api/v1/user/complete-admin-onboarding/${passuid}/${passtoken}/`,
    data
  );

/**
 * This function is the service to get all the store for a host
 *
 * @return {Object}response object
 */

export const getAllStoresForHost = async (params) => {
  return await axiosInstance({
    method: 'get',
    url: `${process.env.REACT_APP_BASE_URL}/api/v1/get-all-stores/`,
  });
};


/**
 * This function is the service to get all the products of a host
 *
 * @return {Object}response object
 */

export const getAllProductsForHost = async () => {
  return await axiosInstance({
    method: 'get',
    url: `${process.env.REACT_APP_BASE_URL}/api/v1/get-host-products/`,
  });
};

export const getStoreHostRewardType = async () =>
  axiosInstance.get(`/api/v1/store/host-reward-type/`);

export const updateStoreHostRewardType = async (data) =>
  axiosInstance.patch(`/api/v1/store/host-reward-type/`, data);

/**
 * This function is used to make an api call for downloading the accounting data
 *
 * @param {object} data data object which is to be passed in the body of the api call
 * @return {object} response object from the api call
 */

export const downloadAccountingData = async (data) => {
  return await axios({
    method: 'get',
    responseType: 'blob',
    headers: {
      'Content-Type': 'text/csv',
      'Authorization': `Bearer ${LocalStorageService.getAccessToken()}`,
    },
    url: `${process.env.REACT_APP_BASE_URL}/api/v1/download-accounting-data/${data}`,
  });
};

/**
*
  * This function is used to update the store URL
  *  
  * @param {string} storeId - The ID of the store to update.
  * @param {object} data - The data to update the store with.
  * @return {object} - The updated store data.
*/
export const updateStoreDemoRescheduleUrl = async (storeId, data) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_BASE_URL}/api/v1/store-url-update?store_id=${storeId}`,
      data
    );
    return response;
  } catch (error) {
    console.error('Error updating store URL:', error);
    throw error;
  }
};

/**
 * Host Recruitment Email Template APIs
 */

/**
 * Get host recruitment email template for a store
 * @param {string} storeId - The store ID
 * @returns {Promise} - API response with template data
 */
export const getHostRecruitmentEmailTemplate = async (storeId) =>
  axiosInstance.get(`/api/v1/stores/${storeId}/email-templates/host-recruitment/get/`);

/**
 * Update existing host recruitment email template
 * @param {string} storeId - The store ID
 * @param {object} data - Template data to update (can be JSON or FormData)
 * @param {boolean} isFormData - Whether data is FormData (for logo uploads)
 * @returns {Promise} - API response
 */
export const updateHostRecruitmentEmailTemplate = async (storeId, data, isFormData = false) => {
  const config = isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
  
  if (isFormData && data instanceof FormData) {
    const templateId = data.get('id') || data.get('template_id');
    if (data.has('email_template_body')) {
      data.set('email_template_body', await processTemplateImages(data.get('email_template_body'), templateId));
    }
    if (data.has('email_template_body_html')) {
      data.set('email_template_body_html', await processTemplateImages(data.get('email_template_body_html'), templateId));
    }
  } else if (!isFormData && data) {
    const templateId = data.id || data.template_id;
    if (data.email_template_body) {
      data.email_template_body = await processTemplateImages(data.email_template_body, templateId);
    }
    if (data.email_template_body_html) {
      data.email_template_body_html = await processTemplateImages(data.email_template_body_html, templateId);
    }
  }
  
  return axiosInstance.put(`/api/v1/stores/${storeId}/email-templates/host-recruitment/update/`, data, config);
};

/**
 * Create new host recruitment email template (requires logo)
 * @param {string} storeId - The store ID
 * @param {FormData} formData - Form data including logo file
 * @returns {Promise} - API response
 */
export const createHostRecruitmentEmailTemplate = async (storeId, formData) => {
  if (formData instanceof FormData) {
    const templateId = formData.get('id') || formData.get('template_id');
    if (formData.has('email_template_body')) {
      formData.set('email_template_body', await processTemplateImages(formData.get('email_template_body'), templateId));
    }
    if (formData.has('email_template_body_html')) {
      formData.set('email_template_body_html', await processTemplateImages(formData.get('email_template_body_html'), templateId));
    }
  }

  return axiosInstance.post(`/api/v1/stores/${storeId}/email-templates/host-recruitment/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

/**
 * Get available template variables for host recruitment emails
 * @param {string} storeId - The store ID
 * @returns {Promise} - API response with variables array
 */
export const getHostRecruitmentTemplateVariables = async (storeId) =>
  axiosInstance.get(`/api/v1/stores/${storeId}/email-templates/HOST_RECRUITMENT/variables/`);

/**
 * Get non-host customers (potential hosts)
 * @param {string} storeId - The store ID
 * @param {Object} params - Query parameters (page, page_size, search, min_spent, max_spent, created_from, created_to)
 * @returns {Promise} - API response with paginated customer data
 */
export const getNonHostCustomers = (storeId, params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const url = `/api/v1/stores/${storeId}/customers/non-hosts/${queryString ? `?${queryString}` : ''}`;
  return axiosInstance.get(url);
};

/**
 * Send host invites to selected customers
 * @param {string} storeId - The store ID
 * @param {Array<string>} customerIds - Array of customer IDs to invite
 * @returns {Promise} - API response
 */
export const sendHostInvites = (storeId, customerIds) => {
  return axiosInstance.post(`/api/v1/stores/${storeId}/customers/send-host-invite/`, {
    customer_ids: customerIds
  });
};



/**
 * Get available flow templates (communication workflows) for the store.
 * @returns {Promise} - API response with the list of flow types
 */
export const getFlowTemplates = () => 
  axiosInstance.get('/api/v1/flows/');

/**
 * Get workflow by flow type ID
 * @param {string} flowId - The flow type ID (UUID)
 * @returns {Promise} - API response with workflow details
 */
export const getWorkflow = (flowId) => 
  axiosInstance.get(`/api/v1/workflows/?flow_id=${flowId}`);

/**
 * Update workflow (creates draft or published version)
 * @param {string} workflowId - The workflow ID
 * @param {object} data - { graph_json, status }
 * @returns {Promise} - API response
 */
export const updateWorkflow = (workflowId, data) => 
  axiosInstance.put(`/api/v1/workflows/${workflowId}/update/`, data);

/**
 * Delete workflow draft version
 * @param {string} workflowId - The workflow ID
 * @returns {Promise} - API response
 */
export const deleteWorkflowDraft = (workflowId) => 
  axiosInstance.delete(`/api/v1/workflows/${workflowId}/delete-drafts/`);

/**
 * Get email or SMS template
 * @param {string} templateId - The template ID
 * @param {string} templateType - Template type (e.g., 'EMAIL' or 'SMS')
 * @returns {Promise} - API response
 */
export const getTemplate = (templateId, templateType) => 
  axiosInstance.get(`/api/v1/templates/?template_id=${templateId}&template_type=${templateType}`);

/**
 * Create a blank email or SMS template with the given UUID.
 * Uses get_or_create on the backend — safe to call even if the template already exists.
 * @param {string} templateId - UUID for the new template
 * @param {string} templateType - 'email' or 'sms'
 * @param {string} name - Optional display name for the new template
 * @returns {Promise} - API response with the template data
 */
export const createTemplate = (templateId, templateType, name = 'New Template') =>
  axiosInstance.post('/api/v1/templates/', { template_id: templateId, template_type: templateType, name });

/**
 * Update email or SMS template
 * @param {string} templateId - The template ID
 * @param {string} templateType - Template type (e.g., 'EMAIL' or 'SMS')
 * @param {object} data - Template update data
 * @returns {Promise} - API response
 */
export const updateTemplate = async (templateId, templateType, data) => {
  if (data && templateType === 'email') {
    // Process all possible HTML/MJML field names — covers both HostRecruitment and Workflow Builder paths
    if (data.body) data.body = await processTemplateImages(data.body, templateId);
    if (data.bodyHtml) data.bodyHtml = await processTemplateImages(data.bodyHtml, templateId);
    if (data.html) data.html = await processTemplateImages(data.html, templateId);
    if (data.mjml) data.mjml = await processTemplateImages(data.mjml, templateId);
    // Workflow Builder publish path uses these field names:
    if (data.html_content) data.html_content = await processTemplateImages(data.html_content, templateId);
    if (data.mjml_content) data.mjml_content = await processTemplateImages(data.mjml_content, templateId);
  }

  return axiosInstance.put(`/api/v1/templates/?template_id=${templateId}&template_type=${templateType}`, data);
};

/**
 * Get list of variables available to a store.
 * When a flowId (FlowType UUID) is supplied, the list is scoped to the
 * variables mapped for that flow type only; otherwise all flows are returned.
 * @param {string} [flowId] - Optional FlowType UUID to scope variables by flow
 * @returns {Promise} - API response with variables list
 */
export const getVariables = (flowId) =>
  axiosInstance.get(
    flowId
      ? `/api/v1/variables/?flow_id=${flowId}`
      : '/api/v1/variables/'
  );

/**
 * Get condition operators for workflow condition nodes.
 * When a dataType is supplied, only operators applicable to that variable
 * data type are returned (e.g. string, number, boolean).
 * @param {string} [dataType] - Optional variable data type filter
 * @returns {Promise} - API response with condition operators list
 */
export const getConditionOperators = (dataType) =>
  axiosInstance.get(
    dataType
      ? `/api/v1/condition-operators/?data_type=${dataType}`
      : '/api/v1/condition-operators/'
  );